import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { Subject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BarcodeScannerService implements OnDestroy {
  private barcodeDetected$ = new Subject<string>();
  private scanCapture: ((barcode: string) => void) | null = null;
  private buffer: string = '';
  private lastKeyTime: number = 0;
  private readonly TIMEOUT_THRESHOLD_MS = 20; // Max time between keys to be considered a scan
  private readonly MIN_BARCODE_LENGTH = 5;

  private boundHandleKeydown: (event: KeyboardEvent) => void;

  constructor(private ngZone: NgZone) {
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.initializeListener();
  }

  get barcodeScanned$(): Observable<string> {
    return this.barcodeDetected$.asObservable();
  }

  captureScans(handler: (barcode: string) => void): () => void {
    this.buffer = '';
    this.scanCapture = handler;
    return () => {
      if (this.scanCapture === handler) {
        this.scanCapture = null;
        this.buffer = '';
      }
    };
  }

  private initializeListener(): void {
    // Run outside Angular zone to avoid triggering change detection on every keypress
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('keydown', this.boundHandleKeydown, true); // true = Capture phase
    });
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (!this.scanCapture && !this.barcodeDetected$.observed) return;
    if (event.ctrlKey || event.altKey || event.metaKey || event.isComposing) {
      this.buffer = '';
      return;
    }
    const currentTime = Date.now();
    const timeDiff = currentTime - this.lastKeyTime;
    this.lastKeyTime = currentTime;

    // Handle 'Enter' - often the termination character for scanners
    if (event?.key === 'Enter') {
      if (timeDiff < this.TIMEOUT_THRESHOLD_MS && this.buffer.length >= this.MIN_BARCODE_LENGTH) {
        event.preventDefault();
        event.stopPropagation();
        this.emitBarcode(this.buffer);
      }
      this.buffer = '';
      return;
    }

    // Ignore non-printable keys (Shift, Ctrl, etc.) except for buffer management
    if (event?.key?.length !== 1) {
      // If we pause too long or hit a special key, reset
      if (timeDiff > this.TIMEOUT_THRESHOLD_MS) {
        this.buffer = '';
      }
      return;
    }

    // Heuristic: If keys come very fast, it's a scanner
    if (timeDiff < this.TIMEOUT_THRESHOLD_MS) {
      // If this is the SECOND character (buffer was length 1), it confirms a scan pattern.
      // We must check if the FIRST character leaked into a focused input and remove it.
      if (this.buffer.length === 1) {
        this.rollbackFirstCharacterLeak(this.buffer);
      }

      this.buffer += event?.key;
      // Isolate the event: prevent it from reaching the focused element
      event.preventDefault();
      event.stopPropagation();
    } else {
      // Slow typing: reset buffer, but keep the current key as potential start
      // Do NOT prevent default here, as it might be manual user typing
      this.buffer = event?.key;
    }
  }

  /**
   * If the first character of the barcode was typed into an input because it looked like
   * manual typing (slow/first key), remove it now that we know a scan is in progress.
   */
  private rollbackFirstCharacterLeak(firstChar: string): void {
    const activeElement = document.activeElement as HTMLInputElement;

    const isInput =
      activeElement &&
      (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') &&
      activeElement.value &&
      !activeElement.readOnly &&
      !activeElement.disabled;

    if (isInput) {
      const position = activeElement.selectionStart ?? activeElement.value.length;
      if (position > 0 && activeElement.value[position - 1] === firstChar) {
        activeElement.value = activeElement.value.slice(0, position - 1) + activeElement.value.slice(position);
        if (activeElement.selectionStart !== null) activeElement.setSelectionRange(position - 1, position - 1);
        activeElement.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  private emitBarcode(barcode: string): void {
    // Re-enter Angular zone to emit the event so components update UI
    this.ngZone.run(() => {
      if (this.scanCapture) this.scanCapture(barcode);
      else this.barcodeDetected$.next(barcode);
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.boundHandleKeydown, true);
  }
}
